import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { clearAdminTokens } from "./adminSession";

const AdminAuthContext = createContext(null);

/**
 * Admin auth context — completely separate from the public AuthProvider.
 * Reads from /api/admin-auth/me which is gated by the admin cookie.
 *
 * State values:
 *   admin === null   → checking
 *   admin === false  → not authenticated as admin
 *   admin === object → authenticated admin
 */
export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin-auth/me");
      setAdmin(data.admin);
      return data.admin;
    } catch (err) {
      setAdmin(false);
      if (process.env.NODE_ENV === "development" && !process.env.REACT_APP_BACKEND_URL) {
        console.warn(
          "[admin] Not signed in. Use /admin/login after npm start with .env.local (empty REACT_APP_BACKEND_URL + proxy).",
        );
      }
      return false;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/admin-auth/logout");
    } catch {
      /* ignore */
    }
    clearAdminTokens();
    setAdmin(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, setAdmin, fetchMe, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
